import {
  Alert,
  Button,
  Card,
  Checkbox,
  Empty,
  Input,
  InputNumber,
  Modal,
  Progress,
  Slider,
  Spin,
  Statistic,
  Tag,
  message,
} from "antd";
import {
  CheckmarkCircleOutline,
  DocumentTextOutline,
  DownloadOutline,
  TimeOutline,
  TrendingUpOutline,
  WalletOutline,
} from "@raresail/react-ionicons";
import { useEffect, useMemo, useState } from "react";
import { downloadPdf } from "../../../utils/pdfDownload";
import { Link } from "react-router-dom";
import styled from "styled-components";
import useGet from "../../../hooks/common/useGet";
import useSet from "../../../hooks/common/useSet";
import { useAuth } from "../../context/AuthContext";
import {
  API_AUTOINVEST,
  API_AUTOINVEST_PRODUCTS,
} from "../../../constants/apis";
import {
  PRIMARY_COLOR,
  primary5,
  primary10,
} from "../../../constants/colors";
import { Heading } from "../../elements/Elements";
import { currencyFormat, formatDate } from "../../../utils/helpers";
import { useTitle } from "../../../hooks/common/useTitle";

// ─── Types ──────────────────────────────────────────────────────
type Product = {
  id: string;
  name: string;
  slug: string;
  tier: string;
  description?: string;
  target_yield_apy: string | number;
  fixed_yield_apy?: string | number | null;
  aum_fee_apy: string | number;
  performance_fee_pct: string | number;
  performance_fee_hurdle_apy: string | number;
  min_term_days: number;
  max_term_days: number;
  min_principal: string | number;
  max_principal?: string | number | null;
};

type Deployment = {
  id: string;
  amount_deployed: string | number;
  expected_return: string | number;
  realized_return: string | number;
  expected_maturity?: string | null;
  status: string;
  loan_id?: string;
  loan_purpose?: string;
  loan_status?: string;
};

type LedgerEntry = {
  id: string;
  created_at: string;
  direction: "in" | "out";
  kind: string;
  amount: string | number;
  balance_after: string | number;
  description: string;
};

type Position = {
  id: string;
  product: Product;
  principal_amount: string | number;
  term_days: number;
  start_date?: string | null;
  maturity_date?: string | null;
  matured_at?: string | null;
  status: string;
  idle_cash: string | number;
  deployed_capital: string | number;
  realized_returns_to_date: string | number;
  realized_losses_to_date: string | number;
  accrued_return_today: string | number;
  auto_rollover: boolean;
  total_value: string | number;
  days_remaining?: number | null;
  created_at: string;
  deployments?: Deployment[];
  recent_ledger?: LedgerEntry[];
};

// ─── Helpers ────────────────────────────────────────────────────
const num = (v: string | number | null | undefined): number =>
  typeof v === "number" ? v : parseFloat(String(v ?? 0)) || 0;

const STATUS_COLORS: Record<string, string> = {
  pending_funding: "default",
  active: "blue",
  maturing: "gold",
  matured: "green",
  withdrawn: "default",
  defaulted: "red",
};

const STATUS_LABELS: Record<string, string> = {
  pending_funding: "Pending Funding",
  active: "Active",
  maturing: "Maturing",
  matured: "Matured",
  withdrawn: "Withdrawn",
  defaulted: "Defaulted",
};

// ─── Main Page ──────────────────────────────────────────────────
export const AutoInvestPage = () => {
  useTitle("AutoInvest");
  const { loadData: loadList, data: listData, loading: listLoading } = useGet();
  const { loadData: loadProducts, data: productsData } = useGet();
  const [openCreate, setOpenCreate] = useState(false);
  const [activePosId, setActivePosId] = useState<string | null>(null);

  useEffect(() => {
    loadList({ api: API_AUTOINVEST });
    loadProducts({ api: API_AUTOINVEST_PRODUCTS });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const positions: Position[] =
    listData?.results ?? (Array.isArray(listData) ? listData : []);
  const products: Product[] =
    productsData?.results ?? (Array.isArray(productsData) ? productsData : []);
  const primaryProduct = products[0];

  const totalAum = positions.reduce(
    (s, p) => s + num(p.principal_amount), 0
  );
  const totalEarned = positions.reduce(
    (s, p) => s + num(p.realized_returns_to_date), 0
  );

  return (
    <Wrapper className="p15">
      <Header>
        <div>
          <Heading className="font20">AutoInvest</Heading>
          <Subtitle>
            Deposit for a fixed term — we automatically invest your money
            across loans and pay you back with returns at maturity.
          </Subtitle>
        </div>
        <Button
          type="primary"
          icon={<TrendingUpOutline />}
          onClick={() => setOpenCreate(true)}
          size="large"
          disabled={!primaryProduct}
          style={{ background: PRIMARY_COLOR }}
        >
          Start AutoInvest
        </Button>
      </Header>

      {/* Summary KPIs */}
      {positions.length > 0 && (
        <SummaryGrid>
          <Card>
            <Statistic title="Total Invested" value={currencyFormat(totalAum)} />
          </Card>
          <Card>
            <Statistic
              title="Total Earned (gross)"
              value={currencyFormat(totalEarned)}
              valueStyle={{ color: "#52c41a" }}
            />
          </Card>
          <Card>
            <Statistic
              title="Active Positions"
              value={positions.filter((p) => p.status === "active").length}
            />
          </Card>
        </SummaryGrid>
      )}

      {/* Positions */}
      {listLoading ? (
        <Centered><Spin /></Centered>
      ) : positions.length === 0 ? (
        <EmptyWrapper>
          <Empty
            description={
              <div className="font13 grey textCenter">
                You don't have any AutoInvest positions yet.<br />
                Start one to put your money to work automatically.
              </div>
            }
          />
        </EmptyWrapper>
      ) : (
        <PositionsList>
          {positions.map((p) => (
            <PositionCard
              key={p.id}
              position={p}
              onOpen={() => setActivePosId(p.id)}
            />
          ))}
        </PositionsList>
      )}

      {primaryProduct && (
        <CreateModal
          product={primaryProduct}
          open={openCreate}
          onClose={() => setOpenCreate(false)}
          onSuccess={() => {
            setOpenCreate(false);
            loadList({ api: API_AUTOINVEST });
          }}
        />
      )}

      {activePosId && (
        <PositionDetailModal
          positionId={activePosId}
          onClose={() => setActivePosId(null)}
        />
      )}
    </Wrapper>
  );
};

// ─── Position card ──────────────────────────────────────────────
const PositionCard = ({
  position,
  onOpen,
}: {
  position: Position;
  onOpen: () => void;
}) => {
  const total = num(position.idle_cash) + num(position.deployed_capital);
  const deployedPct =
    total > 0 ? Math.round((num(position.deployed_capital) / total) * 100) : 0;
  const returnPct =
    num(position.principal_amount) > 0
      ? (num(position.realized_returns_to_date) /
          num(position.principal_amount)) *
        100
      : 0;

  return (
    <Card
      hoverable
      onClick={onOpen}
      style={{ cursor: "pointer" }}
      bodyStyle={{ padding: 18 }}
    >
      <Row>
        <div>
          <div className="font14 semiBold textDark">
            {position.product.name}
          </div>
          <div className="font11 grey">
            {position.term_days} days · started{" "}
            {position.start_date ? formatDate(position.start_date) : "—"}
          </div>
        </div>
        <Tag color={STATUS_COLORS[position.status] || "default"}>
          {STATUS_LABELS[position.status] || position.status}
        </Tag>
      </Row>

      <SplitRow>
        <Cell>
          <Lbl>Principal</Lbl>
          <Val>{currencyFormat(num(position.principal_amount))}</Val>
        </Cell>
        <Cell>
          <Lbl>Earned</Lbl>
          <Val style={{ color: "#2e7d32" }}>
            +{currencyFormat(num(position.realized_returns_to_date))}
            <small className="grey font11 ml5">
              ({returnPct.toFixed(2)}%)
            </small>
          </Val>
        </Cell>
        <Cell>
          <Lbl>Days remaining</Lbl>
          <Val>{position.days_remaining ?? "—"}</Val>
        </Cell>
      </SplitRow>

      <div className="mt15">
        <div className="flexSpaceCenter mb3 font11 grey">
          <span>{deployedPct}% deployed</span>
          <span>
            {currencyFormat(num(position.idle_cash))} idle ·{" "}
            {currencyFormat(num(position.deployed_capital))} working
          </span>
        </div>
        <Progress
          percent={deployedPct}
          size="small"
          showInfo={false}
          strokeColor={PRIMARY_COLOR}
          trailColor={primary10}
        />
      </div>
    </Card>
  );
};

// ─── Create modal ───────────────────────────────────────────────
const CreateModal = ({
  product,
  open,
  onClose,
  onSuccess,
}: {
  product: Product;
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}) => {
  const { uploadData, error, submitting } = useSet();
  const { user } = useAuth();
  const minPrincipal = num(product.min_principal);
  const [principal, setPrincipal] = useState<number>(minPrincipal);
  const [termDays, setTermDays] = useState<number>(180);
  const [accept, setAccept] = useState(false);
  const [pin, setPin] = useState("");

  const targetYield = num(product.target_yield_apy);
  const projectedGross = useMemo(
    () => (principal * targetYield * termDays) / (100 * 365),
    [principal, targetYield, termDays]
  );

  const errMsg = (() => {
    if (!error) return null;
    if (typeof error === "string") return error;
    if (error?.message) return error.message;
    if (error?.error_code === "INSUFFICIENT_BALANCE")
      return "Your wallet balance is too low. Top up first.";
    if (error?.error_code === "KYC_REQUIRED")
      return "Please complete KYC before opening AutoInvest.";
    if (error?.detail) return error.detail;
    return "Couldn't open the position. Please try again.";
  })();

  const submit = () => {
    uploadData({
      api: API_AUTOINVEST,
      method: "POST",
      params: {
        product_slug: product.slug,
        principal_amount: principal,
        term_days: termDays,
        accept_terms: accept,
        auto_rollover: false,
        pin,
      },
      successMessage: { message: "AutoInvest position opened", duration: 3 },
      onSuccess,
    });
  };

  return (
    <Modal
      open={open}
      onCancel={onClose}
      title={`Start AutoInvest — ${product.name}`}
      width={520}
      footer={null}
    >
      <p className="grey font12">
        Target {targetYield}% APY · Variable yield · 1.5% AUM +{" "}
        {product.performance_fee_pct}% performance over{" "}
        {product.performance_fee_hurdle_apy}% hurdle.
      </p>
      <p className="grey font12">
        Capital guaranteed. Returns depend on the loans we invest in.
      </p>

      <FormRow>
        <FormLabel>How much do you want to invest?</FormLabel>
        <InputNumber<number>
          size="large"
          style={{ width: "100%" }}
          min={minPrincipal}
          step={50000}
          value={principal}
          onChange={(v) => setPrincipal(Number(v) || minPrincipal)}
          formatter={(v) =>
            `UGX ${String(v).replace(/\B(?=(\d{3})+(?!\d))/g, ",")}`
          }
          parser={(v) => Number((v || "").replace(/[^\d]/g, ""))}
        />
        <div className="font11 grey mt5">
          Minimum {currencyFormat(minPrincipal)}.
        </div>
      </FormRow>

      <FormRow>
        <FormLabel>How long? ({termDays} days)</FormLabel>
        <Slider
          min={product.min_term_days}
          max={product.max_term_days}
          value={termDays}
          onChange={(v) => setTermDays(v as number)}
          marks={{
            [product.min_term_days]: "30d",
            90: "90d",
            180: "180d",
            365: "1yr",
            [product.max_term_days]: "2yr",
          }}
        />
      </FormRow>

      <Card style={{ background: primary5, border: 0 }}>
        <div className="font11 grey">Projected gross return at maturity</div>
        <div className="font18 semiBold" style={{ color: PRIMARY_COLOR }}>
          {currencyFormat(projectedGross)}
        </div>
        <div className="font11 grey">
          (Target only. Actual return depends on loan performance.)
        </div>
      </Card>

      <FormRow>
        <FormLabel>Transaction PIN</FormLabel>
        <Input.Password
          size="large"
          inputMode="numeric"
          maxLength={4}
          value={pin}
          onChange={(e) => setPin(e.target.value.replace(/\D/g, ""))}
          placeholder="Your 4-digit transaction PIN"
        />
        <div className="font11 grey mt5">
          No PIN yet? Set one under Account → Transaction PIN.
        </div>
      </FormRow>

      <FormRow>
        <Checkbox
          checked={accept}
          onChange={(e) => setAccept(e.target.checked)}
        >
          I accept the{" "}
          <Link to="/legal/terms" target="_blank">
            Terms of Service
          </Link>{" "}
          and understand my capital is not guaranteed.
        </Checkbox>
      </FormRow>

      {errMsg && <Alert type="error" message={errMsg} showIcon className="mb15" />}

      <Button
        type="primary"
        size="large"
        block
        loading={submitting}
        onClick={submit}
        disabled={!accept || principal < minPrincipal || pin.length !== 4}
        style={{ background: PRIMARY_COLOR }}
      >
        Open AutoInvest — {currencyFormat(principal)}
      </Button>
    </Modal>
  );
};

// ─── Position detail modal ──────────────────────────────────────
const PositionDetailModal = ({
  positionId,
  onClose,
}: {
  positionId: string;
  onClose: () => void;
}) => {
  const { loadData, data, loading } = useGet();
  const [downloading, setDownloading] = useState(false);
  useEffect(() => {
    loadData({ api: `${API_AUTOINVEST}${positionId}/` });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [positionId]);
  const pos: Position | null = data ?? null;

  const handleDownloadContract = async () => {
    try {
      setDownloading(true);
      await downloadPdf(
        `autoinvest/${positionId}/contract/`,
        `autoinvest_mandate_${positionId.slice(0, 8)}.pdf`,
      );
    } catch (e: any) {
      message.error(
        e?.response?.data?.detail || "Failed to download contract.",
      );
    } finally {
      setDownloading(false);
    }
  };

  return (
    <Modal
      open
      onCancel={onClose}
      title={pos ? `${pos.product.name} — ${STATUS_LABELS[pos.status]}` : "Position"}
      footer={null}
      width={720}
    >
      {loading || !pos ? (
        <Centered><Spin /></Centered>
      ) : (
        <>
          <KPIRow>
            <KPI
              label="Principal"
              value={currencyFormat(num(pos.principal_amount))}
              icon={<WalletOutline />}
            />
            <KPI
              label="Total Earned"
              value={currencyFormat(num(pos.realized_returns_to_date))}
              icon={<TrendingUpOutline />}
              color="#2e7d32"
            />
            <KPI
              label="Days Remaining"
              value={`${pos.days_remaining ?? "—"}`}
              icon={<TimeOutline />}
            />
          </KPIRow>

          <ContractRow>
            <div>
              <div className="font12 semiBold textDark">Mandate contract</div>
              <div className="font11 grey">
                Your signed agreement showing the principal, term, fee
                schedule, and treasury policy.
              </div>
            </div>
            <Button
              type="default"
              icon={<DownloadOutline />}
              loading={downloading}
              onClick={handleDownloadContract}
            >
              Download PDF
            </Button>
          </ContractRow>

          <SectionTitle>Loans we deployed into ({pos.deployments?.length ?? 0})</SectionTitle>
          {(!pos.deployments || pos.deployments.length === 0) ? (
            <div className="font12 grey">
              Money is idle and waiting for an eligible loan. The picker
              runs daily.
            </div>
          ) : (
            <DeploymentList>
              {pos.deployments.map((d) => {
                const pct =
                  num(d.expected_return) > 0
                    ? Math.round(
                        (num(d.realized_return) / num(d.expected_return)) * 100
                      )
                    : 0;
                return (
                  <DeploymentRow key={d.id}>
                    <div className="flexGrow">
                      <div className="font12 semiBold textDark">
                        Loan #{d.loan_id ?? "—"}
                        <Tag style={{ marginLeft: 6 }} className="font10">
                          {d.status}
                        </Tag>
                      </div>
                      <div className="font11 grey">
                        Deployed {currencyFormat(num(d.amount_deployed))} · expecting{" "}
                        {currencyFormat(num(d.expected_return))}
                        {d.expected_maturity
                          ? ` · matures ${formatDate(d.expected_maturity)}`
                          : ""}
                      </div>
                      <Progress
                        percent={pct}
                        size="small"
                        showInfo={false}
                        strokeColor={pct >= 100 ? "#52c41a" : PRIMARY_COLOR}
                        trailColor={primary10}
                        className="mt5"
                      />
                    </div>
                    <div className="textEnd">
                      <div className="font11 grey">repaid</div>
                      <div className="font13 semiBold">{pct}%</div>
                    </div>
                  </DeploymentRow>
                );
              })}
            </DeploymentList>
          )}

          <SectionTitle>Recent activity</SectionTitle>
          <LedgerList>
            {(pos.recent_ledger || []).slice(0, 10).map((e) => (
              <LedgerRow key={e.id}>
                <div style={{ flex: 1 }}>
                  <div className="font12 textDark">
                    {prettyKind(e.kind)}
                  </div>
                  <div className="font11 grey">{formatDate(e.created_at)}</div>
                </div>
                <div
                  className="font12 semiBold"
                  style={{
                    color: e.direction === "in" ? "#2e7d32" : "#c62828",
                  }}
                >
                  {e.direction === "in" ? "+" : "−"}
                  {currencyFormat(num(e.amount))}
                </div>
              </LedgerRow>
            ))}
          </LedgerList>
        </>
      )}
    </Modal>
  );
};

const prettyKind = (k: string) =>
  k.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

// ─── Small UI bits ──────────────────────────────────────────────
const KPI = ({
  label,
  value,
  icon,
  color,
}: {
  label: string;
  value: string;
  icon?: any;
  color?: string;
}) => (
  <KPICard>
    {icon && <div className="font18 grey">{icon}</div>}
    <div className="font11 grey">{label}</div>
    <div
      className="font16 semiBold textDark"
      style={color ? { color } : undefined}
    >
      {value}
    </div>
  </KPICard>
);

// ─── Styles ─────────────────────────────────────────────────────
const Wrapper = styled.div`
  max-width: 880px;
  margin: 0 auto;
`;
const Header = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  margin-bottom: 18px;
  gap: 12px;
  @media (max-width: 600px) {
    flex-direction: column;
  }
`;
const Subtitle = styled.div`
  color: #64748b;
  font-size: 12px;
  margin-top: 4px;
  max-width: 540px;
`;
const SummaryGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 12px;
  margin-bottom: 18px;
  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
`;
const PositionsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;
const Centered = styled.div`
  display: flex;
  justify-content: center;
  padding: 40px 0;
`;
const EmptyWrapper = styled.div`
  padding: 40px 0;
`;
const Row = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
`;
const SplitRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  gap: 12px;
  margin-top: 14px;
`;
const Cell = styled.div``;
const Lbl = styled.div`
  font-size: 11px;
  color: #64748b;
`;
const Val = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: #0f172a;
  margin-top: 2px;
`;
const FormRow = styled.div`
  margin: 14px 0;
`;
const FormLabel = styled.div`
  font-size: 12px;
  font-weight: 600;
  color: #0f172a;
  margin-bottom: 6px;
`;
const KPIRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  gap: 12px;
  margin-bottom: 18px;
`;
const KPICard = styled.div`
  border: 1px solid ${primary5};
  border-radius: 10px;
  padding: 12px;
  background: #fff;
`;
const ContractRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 12px 14px;
  border: 1px solid ${primary5};
  border-radius: 10px;
  background: #fafbff;
  margin-bottom: 6px;
`;
const SectionTitle = styled.div`
  font-weight: 700;
  color: #0f172a;
  margin: 20px 0 10px;
`;
const DeploymentList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;
const DeploymentRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 12px;
  border: 1px solid #f1f5f9;
  border-radius: 8px;
`;
const LedgerList = styled.div`
  display: flex;
  flex-direction: column;
  border-top: 1px solid #f1f5f9;
`;
const LedgerRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 0;
  border-bottom: 1px solid #f1f5f9;
`;
